import { type Brand, type VideoPosition, videoUnitPath, ADS_ENABLED, kvKey } from "@/lib/ads/config";

/**
 * Build the IMA / VAST ad tag for a player. `vpos` is set here because GAM only
 * auto-populates it when Ad Rules are used.
 */
export function buildImaTag(
  brand: Brand,
  position: VideoPosition,
  targeting: Record<string, string | string[]>,
  opts: { sizes?: string; companions?: string; descriptionUrl?: string } = {},
): string | null {
  if (!ADS_ENABLED) return null;
  const cust = Object.entries(targeting)
    .map(([k, v]) => `${k}=${(Array.isArray(v) ? v : [v]).map(encodeURIComponent).join(",")}`)
    .join("&");
  const outstream = position === "Outstream";
  const q = new URLSearchParams({
    iu: videoUnitPath(brand, position),
    sz: opts.sizes ?? "640x480|1280x720",
    // IAB placement (mandatory in Ad Manager since 2024): 1 = in-stream, 2 = accompanying
    // content (an outstream in an article); an outstream also declares muted autoplay.
    plcmt: outstream ? "2" : "1",
    ...(outstream ? { vpmute: "1", vpa: "auto" } : { vpos: position.toLowerCase() }),
    cust_params: cust,
    gdfp_req: "1",
    output: "vast",
    env: "vp",
    unviewed_position_start: "1",
    correlator: String(Date.now()),
  });
  if (opts.companions) q.set("ciu_szs", opts.companions);
  if (opts.descriptionUrl) q.set("description_url", opts.descriptionUrl);
  return `https://pubads.g.doubleclick.net/gampad/ads?${q.toString()}`;
}

/** The page-level key-values as the provider sends them (for the video tag). */
export function videoTargetingFromDom(): Record<string, string | string[]> {
  const g = typeof window !== "undefined" ? window.googletag : undefined;
  const out: Record<string, string | string[]> = {};
  if (g?.pubads) for (const k of ["section", "page", "usertype", "channel", "keyword", "stockcode", "env", "tier", "test"].map(kvKey)) { const v = g.pubads().getTargeting(k); if (v?.length) out[k] = v; }
  return out;
}
